import { Link } from "react-router-dom";

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-3">{title}</h2>
      <div className="text-text-secondary text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function ZCBadge({ value }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-500/15 border border-violet-500/30 text-violet-300 text-xs font-medium">
      {value} ZC
    </span>
  );
}

function Pill({ tone = "slate", children }) {
  const map = {
    slate:   "bg-bg-secondary text-text-secondary border-bg-secondary",
    violet:  "bg-violet-500/15 text-violet-300 border-violet-500/30",
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber:   "bg-amber-500/15 text-amber-300 border-amber-500/30",
    blue:    "bg-blue-500/15 text-blue-300 border-blue-500/30",
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded border text-[11px] font-medium ${map[tone]}`}>
      {children}
    </span>
  );
}

function Mockup({ children, label }) {
  return (
    <div className="my-3 rounded-lg border border-bg-secondary bg-bg-main p-3">
      {label && (
        <p className="text-[10px] uppercase tracking-wider text-text-secondary mb-2">
          {label}
        </p>
      )}
      {children}
    </div>
  );
}

export default function AIGuide() {
  return (
    <div className="min-h-screen bg-bg-main py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/dashboard" className="text-text-secondary hover:text-text-primary text-sm">
            ← Back to dashboard
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-3xl">✨</span>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary">AI features &amp; Zencredits</h1>
          </div>
          <p className="text-text-secondary text-sm mt-2">
            Everything the AI-powered chips, buttons, and tags do in ZenCricketData — what they cost, when to use them, and where to find them in the app.
          </p>
        </div>

        <nav className="mb-8 bg-bg-primary border border-bg-secondary rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-text-secondary mb-2">On this page</p>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm list-decimal list-inside">
            <li><a href="#zencredits" className="text-violet-300 hover:underline">What are Zencredits?</a></li>
            <li><a href="#ai-tag" className="text-violet-300 hover:underline">AI Tag — auto-fill chips</a></li>
            <li><a href="#ai-suggest" className="text-violet-300 hover:underline">AI Suggest — per-ball assist</a></li>
            <li><a href="#scorer-chat" className="text-violet-300 hover:underline">Scorer chat assistant</a></li>
            <li><a href="#match-report" className="text-violet-300 hover:underline">AI match report</a></li>
            <li><a href="#regenerate-commentary" className="text-violet-300 hover:underline">Regenerate ball commentary</a></li>
            <li><a href="#voice" className="text-violet-300 hover:underline">Voice scoring (AI-powered)</a></li>
            <li><a href="#wallet" className="text-violet-300 hover:underline">Wallet, low-balance warnings, topup</a></li>
            <li><a href="#privacy" className="text-violet-300 hover:underline">Privacy &amp; what's sent to the model</a></li>
            <li><a href="#faq" className="text-violet-300 hover:underline">FAQ</a></li>
          </ol>
        </nav>

        <Section id="zencredits" title="What are Zencredits?">
          <p>
            <strong className="text-text-primary">Zencredits (ZC)</strong> are the in-app currency for every AI-powered feature in
            ZenCricketData. Roughly <strong className="text-text-primary">1 ZC ≈ ₹1</strong> at retail.
          </p>
          <p>Your plan gives you a monthly bundle that refreshes on renewal:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-bg-secondary rounded-lg overflow-hidden">
              <thead className="bg-bg-primary">
                <tr>
                  <th className="text-left p-2 text-text-primary">Plan</th>
                  <th className="text-left p-2 text-text-primary">Monthly ZC</th>
                  <th className="text-left p-2 text-text-primary">Typical use</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-secondary text-text-secondary">
                <tr><td className="p-2">Basic</td><td className="p-2">400 ZC</td><td className="p-2">~25 AI-Tag passes, light voice usage</td></tr>
                <tr><td className="p-2">Intermediate</td><td className="p-2">1000 ZC</td><td className="p-2">Heavy AI-Tag + match reports + scorer chat</td></tr>
                <tr><td className="p-2">Advanced</td><td className="p-2">2500 ZC</td><td className="p-2">Full AI-Suggest workflow + commentary regen</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Out of credits mid-match? Top up any time at{" "}
            <Link to="/account/wallet" className="text-emerald-400 hover:underline">/account/wallet</Link>.
            The wallet pill in the header of every key screen shows your current balance.
          </p>
          <Mockup label="Wallet pill (top-right header)">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-violet-500/30 bg-violet-500/10 text-violet-300 text-xs">
              <span>💎</span>
              <span className="font-mono">347 ZC</span>
            </div>
          </Mockup>
        </Section>

        <Section id="ai-tag" title={<>1. AI Tag <ZCBadge value={15} /> — auto-fill missing chips on every ball</>}>
          <p>
            <strong className="text-text-primary">What it does:</strong> walks through every ball in the innings
            and fills any of these analytics fields that are blank:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong className="text-text-primary">Stroke</strong> — drive / pull / cut / sweep / defensive…</li>
            <li><strong className="text-text-primary">Shot connection</strong> — middled / edged / mistimed / left…</li>
            <li><strong className="text-text-primary">Delivery type</strong> — yorker / good-length / short / full-toss…</li>
            <li><strong className="text-text-primary">Foot movement</strong> — front / back / dance / advance…</li>
            <li><strong className="text-text-primary">Keeper activity</strong> — collected / parried / dive…</li>
          </ul>
          <p>
            <strong className="text-text-primary">When to use:</strong> after you've finished scoring, or
            mid-innings if you left chips empty to keep up with the rhythm. AI Tag is the fastest way to
            make the scorecard "complete" for a customer-facing release.
          </p>
          <p><strong className="text-text-primary">Where you'll find the button:</strong></p>
          <Mockup label="Matches list — per-row button">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-text-secondary">SRH vs MI · T20 · 2026-06-04</span>
              <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-500/15 border border-violet-500/30 text-violet-300">
                ✨ AI Tag · 15 ZC
              </span>
            </div>
          </Mockup>
          <Mockup label="Scoring console action bar (top-right)">
            <div className="flex items-center gap-2 text-xs">
              <Pill tone="emerald">UNDO</Pill>
              <Pill tone="blue">EDIT BALL</Pill>
              <Pill tone="violet">✨ AI Tag · 15 ZC</Pill>
            </div>
          </Mockup>
          <p>
            Also available on the <strong className="text-text-primary">QC page</strong> (Quality Control / bulk review)
            and from the <strong className="text-text-primary">Scorecard</strong> for finalised matches.
          </p>
          <p>
            <strong className="text-text-primary">What happens after you click:</strong>
          </p>
          <ol className="list-decimal list-inside ml-2 space-y-1">
            <li>Confirmation modal shows: "X balls need tagging — proceed? Cost: 15 ZC"</li>
            <li>Click <strong className="text-text-primary">Confirm</strong> — a progress bar walks through the innings</li>
            <li>Each ball's missing chips fill in real-time (you can watch the chips light up)</li>
            <li>Done in ~10–30 seconds for a full T20 innings</li>
            <li>Wallet pill ticks down by 15 ZC</li>
          </ol>
          <p>
            <strong className="text-text-primary">Already-filled chips are NEVER overwritten.</strong> AI Tag respects manual
            scorer input as the source of truth.
          </p>
        </Section>

        <Section id="ai-suggest" title={<>2. AI Suggest <ZCBadge value={7} /> — per-ball assist while scoring</>}>
          <p>
            <strong className="text-text-primary">Closed beta</strong> — currently enabled for Manivannan + Vikram only.
            Coming to all paid plans soon.
          </p>
          <p>
            <strong className="text-text-primary">What it does:</strong> on the live scoring screen, while the
            current ball form is open and unsaved, click <Pill tone="violet">✨ Suggest · 7 ZC</Pill>. The system
            grabs ~8 frames of video around the current ball's timestamps, sends them to Claude with the match
            context, and fills the entire ball-form accordion in one shot:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>Runs (0 / 1 / 2 / 4 / 6)</li>
            <li>Wide / no-ball / bye / leg-bye flags</li>
            <li>Stroke + shot connection + foot movement</li>
            <li>Delivery type + line + length</li>
            <li>Wicket type + dismissed-player guess (if any)</li>
            <li>Wagon-wheel coordinates + pitch-map coordinates</li>
          </ul>
          <p>
            You still need to <strong className="text-text-primary">review and click Save</strong> — AI Suggest doesn't
            auto-submit. Treat it as a "first draft" the scorer verifies.
          </p>
          <p>
            <strong className="text-text-primary">When it works best:</strong> matches with a recorded video stream
            (master recording uploaded, or live MediaRecorder running). Without video, AI Suggest can't fire — the
            button greys out.
          </p>
        </Section>

        <Section id="scorer-chat" title={<>3. Scorer chat <ZCBadge value={2} /> per message — your rules / undo / debug assistant</>}>
          <p>
            <strong className="text-text-primary">Where:</strong> bottom-right floating chat bubble on the scoring console.
            Click → opens a chat panel that accepts <strong className="text-text-primary">voice or text</strong>.
          </p>
          <p><strong className="text-text-primary">What you can ask:</strong></p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>"What's the rule for retired hurt?" → answers from the cricket laws</li>
            <li>"Undo the last ball" → triggers the undo flow</li>
            <li>"Edit ball 12.3 to a wide" → opens the edit dialog on that ball</li>
            <li>"What's the partnership right now?" → reads current match state</li>
            <li>"Why is the strike not rotating?" → debugs the last few balls' physical_runs math</li>
          </ul>
          <p>
            <strong className="text-text-primary">Cost:</strong> 2 ZC per message sent. Replies from the model
            don't cost extra — only your outbound messages.
          </p>
          <Mockup label="Floating chat bubble (bottom-right)">
            <div className="flex items-end justify-end">
              <button className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-200 text-xs hover:bg-violet-500/30">
                💬 Ask scorer
              </button>
            </div>
          </Mockup>
        </Section>

        <Section id="match-report" title={<>4. AI match report <ZCBadge value={30} /> — narrative summary</>}>
          <p>
            <strong className="text-text-primary">Where:</strong> on the public Scorecard page after a match has ended,
            an <Pill tone="violet">✨ Generate match report · 30 ZC</Pill> button appears below the FoW table.
          </p>
          <p>The report includes:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>2–3 paragraph match narrative — turning points, partnerships, bowling spells</li>
            <li>Best batter / best bowler highlights with numbers</li>
            <li>Man of the Match nomination with reasoning</li>
            <li>One-line headline you can paste straight into a tweet or club newsletter</li>
          </ul>
          <p>Saved on the match — you can re-read it any time without paying again. Regenerate costs another 30 ZC.</p>
        </Section>

        <Section id="regenerate-commentary" title={<>5. Regenerate ball commentary <ZCBadge value={3} /></>}>
          <p>
            Every ball gets an auto-generated commentary line by default (template-based, free). If a line reads
            stiff or you want a richer phrasing, open the per-ball edit dialog → Commentary tab →
            <Pill tone="violet">✨ Regenerate · 3 ZC</Pill> button.
          </p>
          <p>
            Useful for the 3–5 "key" balls of an innings — the wicket, the six off the last ball,
            the dropped catch — that you want sharper copy on for the public scorecard.
          </p>
        </Section>

        <Section id="voice" title="6. Voice scoring (AI-powered transcription)">
          <p>
            Voice scoring lets you call out the ball into a mic and the system auto-fills the chip grid.
            The mic icon lives in the header bar of the scoring console.
          </p>
          <p><strong className="text-text-primary">How it works under the hood:</strong></p>
          <ol className="list-decimal list-inside ml-2 space-y-1">
            <li>Push-to-talk (PTT) hold — Whisper STT transcribes the audio</li>
            <li>Claude parses the transcript into structured ball data (runs, extras, dismissal, stroke…)</li>
            <li>Chips fill in the form for you to verify + Save</li>
          </ol>
          <p>
            <strong className="text-text-primary">Cost:</strong> bundled into your plan's monthly voice quota. If you
            exceed quota, falls back to ZC at the equivalent rate (varies by plan).
          </p>
          <p>
            Voice + scoring tips, vocabulary cheat sheet, and noise-gate toggle live in the right pane of the
            scoring console under <strong className="text-text-primary">🎤 Voice cheat sheet</strong>.
          </p>
        </Section>

        <Section id="wallet" title="7. Wallet, low-balance warnings, and topup">
          <p>The system surfaces ZC status in three places to avoid surprise blocks mid-match:</p>
          <ol className="list-decimal list-inside ml-2 space-y-2">
            <li>
              <strong className="text-text-primary">Always-visible wallet pill</strong> in the header of every
              key screen (dashboard, matches list, scoring console, scorecard). Shows current balance.
            </li>
            <li>
              <strong className="text-text-primary">Low-balance toast</strong> when balance drops below 20 ZC.
              Non-blocking — you can dismiss and keep working. Click the toast to open the wallet.
            </li>
            <li>
              <strong className="text-text-primary">Blocking modal</strong> if you click a feature you can't afford
              (e.g. trying AI Tag with only 8 ZC). The modal explains the deficit and routes you to
              <Link to="/account/wallet" className="text-emerald-400 hover:underline"> /account/wallet</Link> to top up.
            </li>
          </ol>
          <p>
            Topups are paid via Razorpay, instant credit, and never expire (unlike monthly plan ZC which resets
            on renewal — unused plan ZC is lost; topup ZC carries forward).
          </p>
        </Section>

        <Section id="privacy" title="8. Privacy — what's sent to the AI model">
          <p>For each AI feature, here's exactly what leaves your browser / our servers:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong className="text-text-primary">AI Tag:</strong> ball-level structured data only (runs, wagon coords, existing chips). No video frames, no PII beyond player names.</li>
            <li><strong className="text-text-primary">AI Suggest:</strong> ~8 video frames around the current ball + match context. Frames are sent over HTTPS to Anthropic; not retained by us after the call.</li>
            <li><strong className="text-text-primary">Scorer chat:</strong> your typed/spoken message + the current match state snapshot.</li>
            <li><strong className="text-text-primary">Voice scoring:</strong> audio clip → Groq Whisper for STT; transcript → Claude for parsing. Audio not stored long-term by us; transcripts are stored on the match for the auto-commentary feature.</li>
          </ul>
          <p>Nothing is used to train the model. Anthropic's no-training policy applies to all API traffic from this app.</p>
        </Section>

        <Section id="faq" title="FAQ">
          <div className="space-y-3">
            <div>
              <p className="text-text-primary font-medium">Do unused monthly Zencredits roll over?</p>
              <p>No — plan ZC resets on each renewal. Topup ZC carries forward indefinitely.</p>
            </div>
            <div>
              <p className="text-text-primary font-medium">Can I refund a bad AI Tag pass?</p>
              <p>Yes — open the per-match dispute flow on the scorecard or contact admin. Refunds are credited back to your wallet as ZC.</p>
            </div>
            <div>
              <p className="text-text-primary font-medium">Why is AI Suggest greyed out for me?</p>
              <p>Closed beta until rollout completes. Also requires the match to have a video recording (master upload or live MediaRecorder running).</p>
            </div>
            <div>
              <p className="text-text-primary font-medium">Can AI Tag overwrite my manual chips?</p>
              <p>No. AI Tag only fills <em>missing</em> chips. Your manual scoring is always the source of truth.</p>
            </div>
            <div>
              <p className="text-text-primary font-medium">How accurate is AI Suggest?</p>
              <p>Currently ~80% accuracy on runs / extras / stroke for clear video. Always review before saving — treat it as a fast first draft.</p>
            </div>
          </div>
        </Section>

        <div className="mt-10 bg-bg-primary border border-bg-secondary rounded-xl p-5 space-y-2">
          <p className="text-sm text-text-secondary">
            Looking for non-AI scoring instructions? See the{" "}
            <Link to="/help/user-guide" className="text-emerald-400 hover:underline">user guide</Link>
            {" "}— full reference for every button, chip, and modal in the app.
          </p>
          <p className="text-sm text-text-secondary">
            Stuck or want a refund? Email{" "}
            <a href="mailto:a.manivannan@sportingzenminds.com" className="text-emerald-400 hover:underline">
              admin
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
}
